/**
 * LanguageSwitcher — Tempora i18n v2.0
 *
 * Compact language toggle button (ES ↔ EN).
 * Persists choice in localStorage via the i18n engine.
 * Designed to be placed in the Sidebar footer or Profile Preferences.
 */
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/components/auth-context'

const LANGUAGES = [
  { code: 'es', label: 'ES', flag: '🇪🇸' },
  { code: 'en', label: 'EN', flag: '🇬🇧' },
] as const

interface LanguageSwitcherProps {
  className?: string
  onLanguageChange?: (code: string) => void
}

export function LanguageSwitcher({ className, onLanguageChange }: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const { user } = useAuth()
  const currentLang = i18n.language?.slice(0, 2) ?? 'es'

  const handleChange = (code: string) => {
    if (code !== currentLang) {
      i18n.changeLanguage(code)
      onLanguageChange?.(code)

      if (user) {
        // Update custom user metadata in Auth (fire-and-forget)
        supabase.auth.updateUser({
          data: { language: code }
        }).catch(err => console.error("Error updating auth user metadata:", err))

        // Update profiles table (fire-and-forget)
        supabase
          .from('profiles')
          .update({ language: code })
          .eq('id', user.id)
          .then(({ error }) => {
            if (error) console.error("Error updating profile language:", error)
          })
      }
    }
  }

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-xl bg-zinc-100 dark:bg-zinc-800 p-1',
        className
      )}
    >
      {LANGUAGES.map(({ code, label, flag }) => (
        <button
          key={code}
          onClick={() => handleChange(code)}
          className={cn(
            'flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all duration-200',
            currentLang === code
              ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm'
              : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
          )}
          aria-label={`Switch language to ${label}`}
          aria-pressed={currentLang === code}
        >
          <span>{flag}</span>
          <span>{label}</span>
        </button>
      ))}
    </div>
  )
}
